import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useChat } from '../context/ChatProvider';
import RoomForm from './RoomForm';
import ConfirmationModal from '../styled/ConfirmationModal';
import axios from 'axios';
import RoomItem from './RoomItem';
import { useTranslation } from "react-i18next";
import { GiExitDoor } from "react-icons/gi";
import { AiFillSetting, AiOutlineBars } from "react-icons/ai";
import { googleLogout } from '@react-oauth/google';
import useChatActions from "../hooks/useChatActions";

/* ─── styled ─── */

const RoomListContainer = styled.div`
    --space: 0.8em;
    --horizontal-space: 1.2rem;

    display: flex;
    flex-direction: column;
    width: 340px;
    min-width: 300px;
    height: 100%;
    overflow: auto;
    background: rgba(15, 12, 41, 0.85);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border-right: 1px solid rgba(255, 255, 255, 0.06);
    color: #fff;
    z-index: 2;

    /* custom scrollbar */
    &::-webkit-scrollbar { width: 5px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 3px; }

    & h3 {
        font-size: 1.1em;
        font-weight: 600;
        padding: 0.9em var(--horizontal-space);
    }

    @media (max-width: 820px) {
        position: absolute;
        opacity: ${props => props.open ? '1' : '0'};
        pointer-events: ${props => props.open ? 'auto' : 'none'};
        right: 0;
        width: 100%;
        min-width: unset;
        border-radius: 0;
        z-index: 3;
        transition: opacity 0.2s ease;
    }
`;

const HeaderBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem var(--horizontal-space);
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    background: rgba(255, 255, 255, 0.02);
    flex-shrink: 0;
    gap: 0.5rem;
`;

const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 0.4rem;
`;

const HeaderRight = styled.div`
    display: flex;
    align-items: center;
    gap: 0.4rem;
`;

const CreateButton = styled.button`
    padding: 0.5rem 1.2rem;
    border-radius: 12px;
    border: none;
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    color: white;
    font-weight: 600;
    font-size: 0.8rem;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 3px 12px rgba(99, 102, 241, 0.3);
    white-space: nowrap;

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 5px 18px rgba(99, 102, 241, 0.45);
    }
`;

const IconButton = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.2em;
    height: 2.2em;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.06);
    cursor: pointer;
    transition: all 0.2s ease;
    color: rgba(255, 255, 255, 0.5);

    &:hover {
        background: rgba(255, 255, 255, 0.1);
        color: white;
    }

    & svg { fill: currentColor; }
`;

const ConversationSection = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 0.5rem 0;

    & ul {
        list-style: none;
        padding: 0;
        margin: 0;
    }
`;

/* ─── component ─── */

const RoomList = ({ showLoginModal }) => {
    const { socket, joinRoom, currentRoom, setCurrentRoom, rooms, setRooms, isNavOpen, user, blockSending, setLoadingMessages, setActiveView, setIsNavOpen, isMobile } = useChat();
    const { logout } = useChatActions();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [roomIdToDelete, setRoomIdToDelete] = useState(null);
    const [roomNameToDelete, setRoomNameToDelete] = useState(null);
    const [showLogoutModal, setShowLogoutModal] = useState(false);

    const { t } = useTranslation();
    const isGuest = user?.guest;
    const filteredRooms = rooms || [];

    const handleRoomClick = (roomID) => { joinRoom(roomID); };

    const handleDeleteRoom = async (roomId) => {
        try {
            const response = await axios.delete(`${process.env.REACT_APP_SERVER}/rooms/${roomId}`, { withCredentials: true });
            if (response.data.success) {
                setRooms((prevRooms) => prevRooms.filter((room) => room.id !== roomId));
            }
        } catch (err) {
            console.error("Error:", err);
        }
    };

    const addNewRoom = (newRoom) => {
        setRooms([...rooms, newRoom]);
        setLoadingMessages(true);
        setCurrentRoom(newRoom);
        sessionStorage.setItem('currentRoomId', newRoom.id);
        sessionStorage.setItem('currentRoom', JSON.stringify(newRoom));
        socket.emit('join-room', { roomID: newRoom.id, roomLanguages: newRoom.languages, userJoining: user });
        setActiveView("chat");
        setIsNavOpen(false);
        window.scrollTo(0, 0);
    };

    const updateRoomImage = (roomId, newImage) => {
        setRooms(prevRooms => prevRooms.map(room =>
            room.id === roomId ? { ...room, image: newImage } : room
        ));
    };

    const toggleCreateRoomModal = () => {
        if (isGuest) { showLoginModal("To create new babbles, you need an account. Create one or sign in below."); return; }
        if (blockSending) return;
        setIsModalOpen(!isModalOpen);
    };

    const handleAccountClick = () => {
        setActiveView("account");
        window.scrollTo(0, 0);
    };

    const handleLogoutConfirmation = () => {
        setShowLogoutModal(false);
        const userLoggedInWithGoogle = sessionStorage.getItem("userLoggedInWithGoogle");
        if (userLoggedInWithGoogle === "TRUE") {
            googleLogout();
        }
        logout();
    };

    const handleConversationListClick = () => {
        setIsNavOpen(false);
        setActiveView("chat");
        window.scrollTo(0, 0);
    };

    return (
        <RoomListContainer open={isNavOpen}>
            <HeaderBar>
                <HeaderLeft>
                    {isMobile && (
                        <IconButton onClick={handleConversationListClick}>
                            <AiOutlineBars size="1.1em" />
                        </IconButton>
                    )}
                    <CreateButton onClick={toggleCreateRoomModal}>
                        {t('RoomListCreate')}
                    </CreateButton>
                </HeaderLeft>

                <HeaderRight>
                    {!isGuest && (
                        <>
                            <IconButton onClick={handleAccountClick}>
                                <Link to="/account" style={{ display: 'flex', color: 'inherit' }}>
                                    <AiFillSetting size="1em" />
                                </Link>
                            </IconButton>
                            <IconButton onClick={(e) => { e.preventDefault(); setShowLogoutModal(true); }}>
                                <GiExitDoor size="1em" />
                            </IconButton>
                        </>
                    )}
                </HeaderRight>
            </HeaderBar>

            {showLogoutModal && (
                <ConfirmationModal
                    onClose={() => setShowLogoutModal(false)}
                    onConfirm={handleLogoutConfirmation}
                    message={t('LogoutModalText')}
                />
            )}

            <RoomForm isOpen={isModalOpen} onNewRoom={addNewRoom} onClose={toggleCreateRoomModal} />

            <ConversationSection>
                <ul>
                    {filteredRooms.map(room => {
                        const { id, isPublic, ownerId } = room;
                        const showDeleteIcon = !isPublic && ownerId === user.id && !isGuest;
                        return (
                            <RoomItem
                                key={id}
                                room={room}
                                isActive={currentRoom?.id === id}
                                handleClick={handleRoomClick}
                                showDeleteIcon={showDeleteIcon}
                                handleDelete={(id, name) => { setRoomIdToDelete(id); setRoomNameToDelete(name); }}
                                unreadMessagesCount={room.unreadMessagesCount}
                                updateRoomImage={updateRoomImage}
                            />
                        );
                    })}
                </ul>
            </ConversationSection>

            {roomIdToDelete && (
                <ConfirmationModal
                    onClose={() => setRoomIdToDelete(null)}
                    onConfirm={() => { handleDeleteRoom(roomIdToDelete); setRoomIdToDelete(null); }}
                    message={`${t('RoomListDeleteConfirm', { roomNameToDelete })}`}
                />
            )}
        </RoomListContainer>
    );
};

export default RoomList;
